import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Grid,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";

const LEVELS = ["", "EMR", "EMT", "AEMT", "Paramedic", "CriticalCare"];
const DIFFICULTIES = ["", "easy", "medium", "hard"];
const QUESTION_TYPES = ["", "multichoice", "multiselect", "fill_in_blank", "free_response", "numeric_equation", "custom"];
const STATUSES = ["", "draft", "approved"];
const EXPORT_STATUSES = ["approved", "draft", "all"];
const CATEGORIES = ["all", "institution", "public"];
const TOPIC_OPTIONS = [
  { value: "", label: "All Topics" },
  { value: "Airway/Respiratory", label: "Airway/Respiratory" },
  { value: "Cardiac", label: "Cardiac" },
  { value: "Medical", label: "Medical" },
  { value: "OB/Peds", label: "OB/Peds" },
  { value: "Trauma", label: "Trauma" },
  { value: "Operations", label: "Operations" },
  { value: "Other", label: "Other" },
];

const LOCAL_STORAGE_CONVERSATION_KEY = "question_bank_conversation_id";

function toDisplayText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || fallback;
  if (typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return String(value);
}

function hasSystemAdminRole(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  return roles.some((role) => {
    if (typeof role === "string") return role === "system-admin";
    return role?.key === "system-admin" || role?.name === "system-admin";
  });
}

function hasAdminRole(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  return roles.some((role) => {
    if (typeof role === "string") return role === "admin" || role === "system-admin";
    return role?.key === "admin" || role?.key === "system-admin" || role?.name === "admin" || role?.name === "system-admin";
  });
}

function normalizeTagList(value) {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((tag) => String(tag || "").trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }
  if (typeof value === "string") {
    return Array.from(
      new Set(
        value
          .split(",")
          .map((tag) => tag.trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }
  return [];
}

function QuestionCard({
  question,
  onApprove,
  onApprovePublic,
  onDraft,
  onUpdateDraft,
  onReject,
  onRejectWithComment,
  canPublishPublic = true,
  canReject = false,
}) {
  const [editStem, setEditStem] = useState(question.stem || "");
  const [editing, setEditing] = useState(false);
  const [showRejectComposer, setShowRejectComposer] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [tags, setTags] = useState(() => normalizeTagList(question.tags || question.metadata?.tags || []));
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    setTags(normalizeTagList(question.tags || question.metadata?.tags || []));
  }, [question?.question_id, question?.tags, question?.metadata?.tags]);

  const upsertTag = (value) => {
    const normalized = normalizeTagList(value);
    if (normalized.length === 0) return;
    setTags((prev) => Array.from(new Set([...prev, ...normalized])));
    setNewTag("");
  };

  const removeTag = (tagToRemove) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const questionWithEdits = {
    ...question,
    stem: editStem,
    tags,
  };

  return (
    <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={3} bg="gray.900">
      <HStack spacing={2} mb={2} flexWrap="wrap">
        <Badge colorScheme={question.status === "approved" ? "green" : "orange"}>{question.status || "draft"}</Badge>
        {question.is_public && <Badge colorScheme="purple">public</Badge>}
        {question.institution && <Badge colorScheme="cyan">{question.institution}</Badge>}
        <Badge>{question.level || "Paramedic"}</Badge>
        <Badge>{question.question_type || "multichoice"}</Badge>
        <Badge>{question.difficulty || "medium"}</Badge>
        <Badge>{question.topic || question.domain || "General EMS"}</Badge>
      </HStack>

      {tags.length > 0 && (
        <HStack spacing={1} mb={2} flexWrap="wrap">
          {tags.map((tag) => (
            <HStack key={tag} spacing={1} px={2} py={1} borderRadius="full" bg="gray.700">
              <Text fontSize="xs" color="gray.100">{tag}</Text>
              <Button
                size="xs"
                variant="ghost"
                minW="16px"
                h="16px"
                p={0}
                lineHeight="1"
                onClick={() => removeTag(tag)}
              >
                x
              </Button>
            </HStack>
          ))}
        </HStack>
      )}

      <HStack spacing={2} mb={2}>
        <Input
          size="xs"
          placeholder="Add tag (enter or comma)"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              upsertTag(newTag);
            }
          }}
          onBlur={() => upsertTag(newTag)}
        />
        <Button size="xs" onClick={() => upsertTag(newTag)}>Add Tag</Button>
      </HStack>

      {editing ? (
        <Textarea value={editStem} onChange={(e) => setEditStem(e.target.value)} size="sm" minH="80px" />
      ) : (
        <Text fontSize="sm" color="gray.100" whiteSpace="pre-wrap">{question.stem}</Text>
      )}

      {Array.isArray(question.options) && question.options.length > 0 && (
        <VStack align="stretch" mt={2} spacing={1}>
          {question.options.map((opt, idx) => (
            <Text key={opt.id || idx} fontSize="xs" color="gray.300">{opt.id || String.fromCharCode(65 + idx)}. {opt.text}</Text>
          ))}
        </VStack>
      )}

      <HStack mt={3} spacing={2} flexWrap="wrap">
        {!editing && (
          <Button size="xs" onClick={() => setEditing(true)}>Edit Stem</Button>
        )}
        {editing && (
          <>
            <Button
              size="xs"
              colorScheme="blue"
              onClick={() => {
                onUpdateDraft(questionWithEdits);
                setEditing(false);
              }}
            >
              Save Edit
            </Button>
            <Button size="xs" variant="outline" onClick={() => { setEditStem(question.stem || ""); setEditing(false); }}>
              Cancel
            </Button>
          </>
        )}
        <Button size="xs" colorScheme="green" onClick={() => onApprove(questionWithEdits)}>Approve</Button>
        {canPublishPublic && (
          <Button size="xs" colorScheme="purple" onClick={() => onApprovePublic(questionWithEdits)}>Approve + Public</Button>
        )}
        <Button size="xs" colorScheme="orange" onClick={() => onDraft(questionWithEdits)}>Draft</Button>
      </HStack>

      {canReject && !showRejectComposer && (
        <HStack mt={3}>
          <Button
            size="xs"
            colorScheme="red"
            variant="outline"
            onClick={() => setShowRejectComposer(true)}
          >
            Reject
          </Button>
        </HStack>
      )}

      {canReject && showRejectComposer && (
        <VStack align="stretch" mt={3} spacing={2}>
          <Textarea
            size="xs"
            minH="56px"
            placeholder="Why are you rejecting this question? (optional)"
            value={feedbackReason}
            onChange={(e) => setFeedbackReason(e.target.value)}
          />
          <HStack spacing={2}>
            <Button
              size="xs"
              colorScheme="red"
              onClick={() => {
                if (feedbackReason.trim().length < 4) return;
                onRejectWithComment(questionWithEdits, feedbackReason.trim());
                setFeedbackReason("");
                setShowRejectComposer(false);
              }}
              isDisabled={feedbackReason.trim().length < 4}
            >
              Submit Feedback
            </Button>
            <Button
              size="xs"
              variant="ghost"
              color="gray.300"
              onClick={() => {
                onReject(questionWithEdits);
                setFeedbackReason("");
                setShowRejectComposer(false);
              }}
            >
              Reject Without Feedback
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                setShowRejectComposer(false);
                setFeedbackReason("");
              }}
            >
              Cancel
            </Button>
          </HStack>
        </VStack>
      )}
    </Box>
  );
}

export default function QuestionBankRoute() {
  const user = useUser();
  const isSystemAdmin = hasSystemAdminRole(user);
  const isAdmin = hasAdminRole(user);

  const [level, setLevel] = useState("");
  const [topic, setTopic] = useState("");
  const [customTopic, setCustomTopic] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [desiredCount, setDesiredCount] = useState(5);
  const [desiredCountInput, setDesiredCountInput] = useState("5");
  const [tagsInput, setTagsInput] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [institutions, setInstitutions] = useState([]);

  const [conversationId, setConversationId] = useState(() => localStorage.getItem(LOCAL_STORAGE_CONVERSATION_KEY) || "");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [infoMessage, setInfoMessage] = useState("");
  const [bankTabIndex, setBankTabIndex] = useState(1);
  const [adaptiveTrainingMode, setAdaptiveTrainingMode] = useState(false);
  const [exportFormat, setExportFormat] = useState("gift");
  const [exportStatus, setExportStatus] = useState("approved");
  const [exportProfileId, setExportProfileId] = useState("");
  const [exportProfiles, setExportProfiles] = useState([]);
  const [profileName, setProfileName] = useState("");
  const [sampleExportText, setSampleExportText] = useState("");

  const [{ data: bankData, fetching: loadingBank }, getQuestionBank] = useGlobalAction(api.getQuestionBank);
  const [{ data: institutionsData }, getQuestionBankInstitutions] = useGlobalAction(api.getQuestionBankInstitutions);
  const [{ fetching: chatting }, questionBankChat] = useGlobalAction(api.questionBankChat);
  const [{ fetching: savingQuestion }, upsertQuestionBankItem] = useGlobalAction(api.upsertQuestionBankItem);
  const [{ fetching: exportingBank }, exportQuestionBank] = useGlobalAction(api.exportQuestionBank);
  const [{ fetching: loadingExportProfiles }, getQuestionBankExportProfiles] = useGlobalAction(api.getQuestionBankExportProfiles);
  const [{ fetching: savingExportProfile }, saveQuestionBankExportProfile] = useGlobalAction(api.saveQuestionBankExportProfile);
  const [{ fetching: savingAdaptiveFeedback }, recordQuestionAdaptiveFeedback] = useGlobalAction(api.recordQuestionAdaptiveFeedback);
  const [{ fetching: tuningAdaptive }, runQuestionAdaptiveTuningBatch] = useGlobalAction(api.runQuestionAdaptiveTuningBatch);
  const [{ fetching: loadingQueue }, getQuestionAdaptiveFeedbackQueue] = useGlobalAction(api.getQuestionAdaptiveFeedbackQueue);
  const [{ fetching: reviewingQueue }, reviewQuestionAdaptiveFeedback] = useGlobalAction(api.reviewQuestionAdaptiveFeedback);
  const [adaptivePendingCount, setAdaptivePendingCount] = useState(0);
  const [adaptiveMinItems, setAdaptiveMinItems] = useState(10);
  const [adaptiveTrackedJobsCount, setAdaptiveTrackedJobsCount] = useState(0);
  const [adaptiveCompletedModelCount, setAdaptiveCompletedModelCount] = useState(0);
  const [queueItems, setQueueItems] = useState([]);
  const [queueEdits, setQueueEdits] = useState({});
  const [queueNotes, setQueueNotes] = useState({});
  const [queueError, setQueueError] = useState("");

  useEffect(() => {
    const run = async () => {
      if (!user?.id) return;
      const result = await getQuestionBankInstitutions({});
      if (result?.success) {
        const list = Array.isArray(result.institutions) ? result.institutions : [];
        setInstitutions(list);
        const fallbackInstitution = result.currentInstitution || user?.institution || list[0] || "";
        setSelectedInstitution((prev) => prev || fallbackInstitution);
      }
    };
    run();
  }, [user?.id]);

  const activeInstitution = selectedInstitution || user?.institution || "";

  const activeTopic = topic === "Other" ? customTopic.trim() : topic;

  const addFilterTag = (rawValue) => {
    const normalized = normalizeTagList(rawValue);
    if (normalized.length === 0) return;
    setSelectedTags((prev) => Array.from(new Set([...prev, ...normalized])));
    setTagsInput("");
  };

  const removeFilterTag = (tagToRemove) => {
    setSelectedTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const loadBank = async () => {
    if (!activeInstitution) return;
    await getQuestionBank({
      institution: activeInstitution,
      category,
      includePublic: category !== "institution",
      level: level || undefined,
      topic: activeTopic || undefined,
      difficulty: difficulty || undefined,
      questionType: questionType || undefined,
      status: status || undefined,
      search: search || undefined,
      limit: 250,
      publicLimit: 250,
    });
  };

  useEffect(() => {
    loadBank();
  }, [activeInstitution, category]);

  const loadExportProfiles = async () => {
    if (!activeInstitution) return;
    const result = await getQuestionBankExportProfiles({
      institution: activeInstitution,
    });
    if (result?.success) {
      setExportProfiles(Array.isArray(result.profiles) ? result.profiles : []);
    }
  };

  useEffect(() => {
    loadExportProfiles();
  }, [activeInstitution]);

  const questions = bankData?.questions || [];

  const approved = useMemo(() => questions.filter((q) => q.status === "approved"), [questions]);
  const draft = useMemo(() => questions.filter((q) => q.status === "draft"), [questions]);
  const publicQuestions = useMemo(() => questions.filter((q) => q.is_public === true), [questions]);
  const parsedDesiredCount = Math.max(1, Math.min(50, Number.parseInt(desiredCountInput, 10) || 5));

  const downloadExport = ({ content, filename, mimeType }) => {
    const blob = new Blob([content || ""], { type: mimeType || "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "question-bank-export.txt";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const onExportQuestionBank = async () => {
    if (!activeInstitution) return;
    const result = await exportQuestionBank({
      institution: activeInstitution,
      format: exportProfileId ? undefined : exportFormat,
      profileId: exportProfileId || undefined,
      category: activeTopic || undefined,
      level: level || undefined,
      topic: activeTopic || undefined,
      difficulty: difficulty || undefined,
      questionType: questionType || undefined,
      status: exportStatus === "all" ? "all" : (exportStatus || undefined),
      search: search || undefined,
      limit: 500,
    });

    if (!result?.success) {
      setInfoMessage(result?.message || "Question bank export failed");
      return;
    }

    downloadExport(result);
    setInfoMessage(`Exported ${result.count || 0} questions as ${result.format}${result.profile?.name ? ` using ${result.profile.name}` : ""}.`);
  };

  const onSaveExportProfile = async () => {
    if (!activeInstitution) return;
    if (!sampleExportText.trim()) {
      setInfoMessage("Paste or upload a sample LMS export before saving a profile.");
      return;
    }
    const result = await saveQuestionBankExportProfile({
      institution: activeInstitution,
      name: profileName || "LMS Export Profile",
      sampleExportText,
    });

    if (!result?.success) {
      setInfoMessage(result?.message || "Failed to save LMS export profile");
      return;
    }

    setProfileName("");
    setSampleExportText("");
    setExportProfileId(result.profileId || "");
    await loadExportProfiles();
    setInfoMessage(`Saved export profile ${result.profile?.name || "LMS Export Profile"} (${result.profile?.formatLabel || result.profile?.format || "detected format"}).`);
  };

  const onSampleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setSampleExportText(text);
    if (!profileName) {
      setProfileName(file.name.replace(/\.[^.]+$/, ""));
    }
    event.target.value = "";
  };

  const onSendChat = async () => {
    if (!chatInput.trim() || !activeInstitution) return;

    const message = chatInput.trim();
    const requestId = `qb-${Date.now().toString().slice(-6)}`;
    const requestedCount = parsedDesiredCount;
    setInfoMessage("");
    setChatInput("");
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: message,
        timestamp: new Date().toISOString(),
        requestId,
        requestedCount,
      },
    ]);

    try {
      const result = await questionBankChat({
        conversationId: conversationId || undefined,
        institution: activeInstitution,
        message,
        level: level || undefined,
        topic: activeTopic || undefined,
        tags: selectedTags,
        difficulty: difficulty || undefined,
        questionType: questionType || undefined,
        desiredCount: requestedCount,
        autoSaveDraft: false,
      });

      if (!result?.success) {
        setInfoMessage(toDisplayText(result?.message || result?.error, "Chat failed"));
        return;
      }

      if (result.conversationId && result.conversationId !== conversationId) {
        setConversationId(result.conversationId);
        localStorage.setItem(LOCAL_STORAGE_CONVERSATION_KEY, result.conversationId);
      }

      if (result.status && result.status !== "completed") {
        setInfoMessage(`[${requestId}] ${toDisplayText(result?.message, `Chat status: ${result.status}.`)}`);
        return;
      }

      if (result.assistantMessage) {
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: toDisplayText(result.assistantMessage, ""),
            timestamp: new Date().toISOString(),
            requestId,
            requestedCount,
          },
        ]);
      } else {
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Generated ${result.suggestions?.items?.length || 0} question proposals.`,
            timestamp: new Date().toISOString(),
            requestId,
            requestedCount,
          },
        ]);
      }

      const items = result.suggestions?.items || [];
      setSuggestedItems(items);
      setBankTabIndex(1); // Draft tab
      const countSummary = `${items.length}/${requestedCount} generated`;
      setInfoMessage(
        `[${requestId}] ${countSummary}. Review on the left; use Approve, Draft, or Reject on each question.`
      );
      await loadBank();
    } catch (error) {
      setInfoMessage(`[${requestId}] Chat failed: ${toDisplayText(error, "Unknown error")}`);
    }
  };

  const onChatKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.shiftKey) return; // allow multiline input
    if (!e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      onSendChat();
      return;
    }
    e.preventDefault();
    onSendChat();
  };

  const upsertStatus = async (question, nextStatus, isPublic = false) => {
    const result = await upsertQuestionBankItem({
      institution: activeInstitution,
      status: nextStatus,
      isPublic,
      question,
      name: `${question.topic || question.domain || "Question"} - ${new Date().toISOString()}`,
    });

    if (!result?.success) {
      setInfoMessage(result?.message || "Failed to save question");
      return;
    }

    setInfoMessage(`Question ${nextStatus}${isPublic ? " (public)" : ""}`);
    await loadBank();
  };

  const onApprove = async (question) => upsertStatus(question, "approved", false);
  const onApprovePublic = async (question) => upsertStatus(question, "approved", true);
  const onDraft = async (question) => upsertStatus(question, "draft", false);
  const onUpdateDraft = async (question) => upsertStatus(question, "draft", false);

  const onAdaptiveFeedback = async ({ action, originalQuestion, editedQuestion, explanation }) => {
    const result = await recordQuestionAdaptiveFeedback({
      action,
      originalQuestion,
      editedQuestion,
      explanation,
      institution: activeInstitution,
      conversationId: conversationId || undefined,
    });

    if (!result?.success) {
      setInfoMessage(result?.message || "Failed to save adaptive feedback");
      return;
    }

    setInfoMessage(`Adaptive feedback queued (${action}) as training record ${result.trainingRecordId}`);
    const preview = await runQuestionAdaptiveTuningBatch({
      institution: activeInstitution,
      minItems: adaptiveMinItems,
      previewOnly: true,
    });
    if (preview?.success) {
      setAdaptivePendingCount(preview.pendingCount || 0);
      setAdaptiveTrackedJobsCount(preview.trackedJobsCount || 0);
      setAdaptiveCompletedModelCount(preview.completedModelCount || 0);
    }
  };

  const onReject = async (question) => {
    await onAdaptiveFeedback({
      action: "reject",
      originalQuestion: question,
      explanation: "Rejected by reviewer without additional comment.",
    });
    await upsertStatus(question, "rejected", false);
    setSuggestedItems((prev) => prev.filter((item) => (item.item_id || item.question_id) !== (question.item_id || question.question_id)));
  };

  const onRejectWithComment = async (question, explanation) => {
    await onAdaptiveFeedback({
      action: "reject",
      originalQuestion: question,
      explanation,
    });
    await upsertStatus(question, "rejected", false);
    setSuggestedItems((prev) => prev.filter((item) => (item.item_id || item.question_id) !== (question.item_id || question.question_id)));
  };

  const refreshAdaptiveStatus = async () => {
    if (!isAdmin || !activeInstitution) return;
    const preview = await runQuestionAdaptiveTuningBatch({
      institution: activeInstitution,
      minItems: adaptiveMinItems,
      previewOnly: true,
    });
    if (preview?.success) {
      setAdaptivePendingCount(preview.pendingCount || 0);
      setAdaptiveTrackedJobsCount(preview.trackedJobsCount || 0);
      setAdaptiveCompletedModelCount(preview.completedModelCount || 0);
    }
  };

  const runAdaptiveTune = async () => {
    const result = await runQuestionAdaptiveTuningBatch({
      institution: activeInstitution,
      minItems: adaptiveMinItems,
      previewOnly: false,
    });
    if (!result?.success) {
      setInfoMessage(result?.message || "Failed to start adaptive tuning batch");
      return;
    }
    setInfoMessage(
      `Adaptive tuning started: job ${result.fineTuneJobId} on ${result.baseModel || "gpt-5-mini"} (${result.pendingCount} items). Tracked jobs: ${result.trackedJobsCount || 0}.`
    );
    await refreshAdaptiveStatus();
  };

  useEffect(() => {
    if (!isAdmin || !activeInstitution) return;
    refreshAdaptiveStatus();
  }, [isAdmin, activeInstitution, adaptiveMinItems]);

  const loadAdaptiveQueue = async () => {
    if (!isSystemAdmin) return;
    setQueueError("");
    const result = await getQuestionAdaptiveFeedbackQueue({
      institution: activeInstitution || undefined,
      limit: 200,
    });
    if (!result?.success) {
      setQueueError(result?.message || "Failed to load adaptive feedback queue");
      return;
    }
    setQueueItems(Array.isArray(result.items) ? result.items : []);
  };

  useEffect(() => {
    if (!isSystemAdmin) return;
    loadAdaptiveQueue();
  }, [isSystemAdmin, activeInstitution]);

  const reviewQueueItem = async (item, decision) => {
    const note = String(queueNotes[item.id] || "").trim();
    const editedStem = String(queueEdits[item.id] || "").trim();
    const editedQuestion = decision === "edit_approve"
      ? { ...(item.originalQuestion || {}), stem: editedStem || item?.originalQuestion?.stem || "" }
      : undefined;

    const result = await reviewQuestionAdaptiveFeedback({
      feedbackId: item.id,
      decision,
      explanation: note || undefined,
      editedQuestion,
    });
    if (!result?.success) {
      setInfoMessage(result?.message || "Failed to process queue item");
      return;
    }
    setInfoMessage(result?.message || "Queue item processed.");
    await loadAdaptiveQueue();
    await refreshAdaptiveStatus();
  };

  return (
    <Box p={4} color="white">
      <VStack align="stretch" spacing={4}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">Instructor Question Bank</Text>
          <Text color="gray.400" fontSize="sm">Durable institution chat + draft/approved/public bank with level/topic/difficulty/type filters.</Text>
        </Box>

        {infoMessage && (
          <Alert status="info" bg="blue.900" borderRadius="md">
            <AlertIcon />
            {infoMessage}
          </Alert>
        )}

        <Grid templateColumns={{ base: "1fr", lg: "1.1fr 1fr" }} gap={4}>
          <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={3} bg="gray.800">
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Question Authoring Chat</Text>
              {isAdmin && (
                <VStack align="stretch" spacing={2}>
                  <Checkbox
                    size="sm"
                    colorScheme="orange"
                    isChecked={adaptiveTrainingMode}
                    onChange={(e) => setAdaptiveTrainingMode(e.target.checked)}
                  >
                    Admin Adaptive Training Mode
                  </Checkbox>
                  {adaptiveTrainingMode && (
                    <HStack spacing={2}>
                      <Input
                        size="xs"
                        width="90px"
                        type="number"
                        min={1}
                        value={adaptiveMinItems}
                        onChange={(e) => setAdaptiveMinItems(Number(e.target.value || 10))}
                      />
                      <Text fontSize="xs" color="gray.400">min items</Text>
                      <Badge colorScheme={adaptivePendingCount >= adaptiveMinItems ? "green" : "orange"}>
                        pending: {adaptivePendingCount}
                      </Badge>
                      <Badge colorScheme="blue">jobs: {adaptiveTrackedJobsCount}</Badge>
                      <Badge colorScheme="teal">models: {adaptiveCompletedModelCount}</Badge>
                      <Button size="xs" onClick={refreshAdaptiveStatus} isLoading={tuningAdaptive}>Refresh</Button>
                      <Button
                        size="xs"
                        colorScheme="purple"
                        onClick={runAdaptiveTune}
                        isDisabled={adaptivePendingCount < adaptiveMinItems}
                        isLoading={tuningAdaptive}
                      >
                        Run Tuning Batch
                      </Button>
                    </HStack>
                  )}
                </VStack>
              )}

              {isSystemAdmin && (
                <Select value={activeInstitution} onChange={(e) => setSelectedInstitution(e.target.value)} size="sm">
                  {institutions.map((inst) => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </Select>
              )}

              <HStack>
                <Select value={level} onChange={(e) => setLevel(e.target.value)} size="sm">
                  {LEVELS.map((v) => <option key={v} value={v}>{v || "All Levels"}</option>)}
                </Select>
                <Select value={topic} onChange={(e) => setTopic(e.target.value)} size="sm">
                  {TOPIC_OPTIONS.map((item) => (
                    <option key={item.value || "all"} value={item.value}>{item.label}</option>
                  ))}
                </Select>
              </HStack>
              {topic === "Other" && (
                <Input size="sm" placeholder="Custom topic" value={customTopic} onChange={(e) => setCustomTopic(e.target.value)} />
              )}
              <VStack align="stretch" spacing={1}>
                <Text fontSize="xs" color="gray.400">Tags (optional)</Text>
                <HStack>
                  <Input
                    size="sm"
                    placeholder="Add tags (enter or comma): ventilator, copd, acs"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addFilterTag(tagsInput);
                      }
                    }}
                    onBlur={() => addFilterTag(tagsInput)}
                  />
                  <Button size="sm" onClick={() => addFilterTag(tagsInput)}>Add Tag</Button>
                </HStack>
                {selectedTags.length > 0 && (
                  <HStack spacing={1} flexWrap="wrap">
                    {selectedTags.map((tag) => (
                      <HStack key={tag} spacing={1} px={2} py={1} borderRadius="full" bg="gray.700">
                        <Text fontSize="xs">{tag}</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          minW="16px"
                          h="16px"
                          p={0}
                          lineHeight="1"
                          onClick={() => removeFilterTag(tag)}
                        >
                          x
                        </Button>
                      </HStack>
                    ))}
                  </HStack>
                )}
              </VStack>
              <HStack>
                <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} size="sm">
                  {DIFFICULTIES.map((v) => <option key={v} value={v}>{v || "All Difficulty"}</option>)}
                </Select>
                <Select value={questionType} onChange={(e) => setQuestionType(e.target.value)} size="sm">
                  {QUESTION_TYPES.map((v) => <option key={v} value={v}>{v || "All Question Types"}</option>)}
                </Select>
              </HStack>
              <HStack>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={1}
                  max={50}
                  size="sm"
                  value={desiredCountInput}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^\d]/g, "");
                    setDesiredCountInput(next);
                    const parsed = Number.parseInt(next, 10);
                    if (Number.isFinite(parsed)) {
                      setDesiredCount(Math.max(1, Math.min(50, parsed)));
                    }
                  }}
                  onBlur={() => {
                    const normalized = String(parsedDesiredCount);
                    setDesiredCountInput(normalized);
                    setDesiredCount(parsedDesiredCount);
                  }}
                  placeholder="Question Count"
                />
                <Button size="sm" onClick={loadBank} isLoading={loadingBank}>Refresh Bank</Button>
              </HStack>
              <Text fontSize="xs" color="gray.400">
                Questions to generate per request: {desiredCount}
              </Text>

              {suggestedItems.length > 0 && (
                <>
                  <Divider />
                  <Text fontSize="sm" color="gray.300">Proposed Questions ({suggestedItems.length})</Text>
                  <VStack align="stretch" spacing={2} maxH="340px" overflowY="auto">
                    {suggestedItems.map((q, idx) => (
                      <QuestionCard
                        key={q.item_id || idx}
                        question={{ ...q, status: "draft" }}
                        onApprove={onApprove}
                        onApprovePublic={onApprovePublic}
                        onDraft={onDraft}
                        onUpdateDraft={onUpdateDraft}
                        onReject={onReject}
                        onRejectWithComment={onRejectWithComment}
                        canPublishPublic={true}
                        canReject={true}
                      />
                    ))}
                  </VStack>
                </>
              )}

              <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={2} minH="120px" maxH="220px" overflowY="auto" bg="gray.900">
                {chatMessages.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">Session log: ask for question sets, then review proposed questions above.</Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {chatMessages.map((m, idx) => (
                      <Box key={`${m.timestamp}-${idx}`} p={2} borderRadius="md" bg={m.role === "user" ? "blue.800" : "gray.700"}>
                        <Text fontSize="xs" color="gray.200" mb={1}>{m.role === "user" ? "You" : "Assistant"}</Text>
                        {(m.requestId || m.requestedCount) && (
                          <Text fontSize="xs" color="gray.300" mb={1}>
                            {m.requestId ? `[${m.requestId}] ` : ""}{m.requestedCount ? `requested ${m.requestedCount}` : ""}
                          </Text>
                        )}
                        <Text fontSize="sm" whiteSpace="pre-wrap">{toDisplayText(m.content, "")}</Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              <HStack align="flex-end">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={onChatKeyDown}
                  placeholder="Write one instruction (e.g., generate 8 EMT airway questions)"
                  size="sm"
                />
                <Button colorScheme="blue" onClick={onSendChat} isLoading={chatting}>Send</Button>
              </HStack>
              <Text fontSize="xs" color="gray.500">Press Enter to send.</Text>
            </VStack>
          </Box>

          <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={3} bg="gray.800">
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Durable Question Bank</Text>
              <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={3} bg="gray.900">
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between" align="center">
                    <Text fontWeight="semibold" fontSize="sm">LMS Export</Text>
                    <Button size="xs" onClick={loadExportProfiles} isLoading={loadingExportProfiles}>Refresh Profiles</Button>
                  </HStack>
                  <Text fontSize="xs" color="gray.400">
                    Export approved/draft questions as Moodle GIFT, Moodle XML, or a saved institution LMS profile.
                  </Text>
                  <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={2}>
                    <Select size="sm" value={exportFormat} onChange={(e) => { setExportFormat(e.target.value); setExportProfileId(""); }}>
                      <option value="gift">Moodle GIFT</option>
                      <option value="moodle_xml">Moodle XML</option>
                    </Select>
                    <Select size="sm" value={exportStatus} onChange={(e) => setExportStatus(e.target.value)}>
                      {EXPORT_STATUSES.map((v) => <option key={v} value={v}>{v === "all" ? "All Statuses" : v}</option>)}
                    </Select>
                    <Select size="sm" value={exportProfileId} onChange={(e) => setExportProfileId(e.target.value)}>
                      <option value="">No saved profile</option>
                      {exportProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name || profile.formatLabel || profile.format}
                        </option>
                      ))}
                    </Select>
                  </Grid>
                  <Button size="sm" colorScheme="teal" onClick={onExportQuestionBank} isLoading={exportingBank}>
                    Export Questions
                  </Button>
                  <Divider />
                  <Text fontWeight="semibold" fontSize="sm">Ingest LMS Sample Export</Text>
                  <Text fontSize="xs" color="gray.400">
                    Paste or upload a sample export from an instructor LMS. The detected format is saved to this institution for reuse.
                  </Text>
                  <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={2}>
                    <Input
                      size="sm"
                      placeholder="Profile name, e.g. Southwest Moodle XML"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                    />
                    <Input size="sm" type="file" accept=".xml,.gift,.txt,.csv,.qti" onChange={onSampleFileChange} />
                  </Grid>
                  <Textarea
                    size="sm"
                    minH="96px"
                    placeholder="Paste sample LMS question export here..."
                    value={sampleExportText}
                    onChange={(e) => setSampleExportText(e.target.value)}
                  />
                  <Button size="sm" colorScheme="blue" onClick={onSaveExportProfile} isLoading={savingExportProfile}>
                    Save Institution Export Profile
                  </Button>
                </VStack>
              </Box>
              <HStack>
                <Select value={category} onChange={(e) => setCategory(e.target.value)} size="sm">
                  {CATEGORIES.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
                </Select>
                <Select value={status} onChange={(e) => setStatus(e.target.value)} size="sm">
                  {STATUSES.map((v) => <option key={v} value={v}>{v || "All Status"}</option>)}
                </Select>
                <Input size="sm" placeholder="Search stem/topic" value={search} onChange={(e) => setSearch(e.target.value)} />
                <Button size="sm" onClick={loadBank}>Apply</Button>
              </HStack>

              {loadingBank ? (
                <Flex justify="center" py={8}><Spinner /></Flex>
              ) : (
                <Tabs
                  variant="enclosed"
                  colorScheme="blue"
                  index={bankTabIndex}
                  onChange={(index) => setBankTabIndex(index)}
                >
                  <TabList>
                    <Tab>Approved ({approved.length})</Tab>
                    <Tab>Draft ({draft.length})</Tab>
                    <Tab>Public ({publicQuestions.length})</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel p={2}>
                      <Stack spacing={2} maxH="640px" overflowY="auto">
                        {approved.length === 0 ? (
                          <Text color="gray.500" fontSize="sm">No approved questions found.</Text>
                        ) : approved.map((q) => (
                          <QuestionCard
                            key={`${q.source_record_id}-${q.question_id}`}
                            question={q}
                            onApprove={onApprove}
                            onApprovePublic={onApprovePublic}
                            onDraft={onDraft}
                            onUpdateDraft={onUpdateDraft}
                            onReject={onReject}
                            onRejectWithComment={onRejectWithComment}
                            canPublishPublic={q.institution === activeInstitution}
                            canReject={true}
                          />
                        ))}
                      </Stack>
                    </TabPanel>
                    <TabPanel p={2}>
                      <Stack spacing={2} maxH="640px" overflowY="auto">
                        {draft.length === 0 ? (
                          <Text color="gray.500" fontSize="sm">No draft questions found.</Text>
                        ) : draft.map((q) => (
                          <QuestionCard
                            key={`${q.source_record_id}-${q.question_id}`}
                            question={q}
                            onApprove={onApprove}
                            onApprovePublic={onApprovePublic}
                            onDraft={onDraft}
                            onUpdateDraft={onUpdateDraft}
                            onReject={onReject}
                            onRejectWithComment={onRejectWithComment}
                            canPublishPublic={q.institution === activeInstitution}
                            canReject={true}
                          />
                        ))}
                      </Stack>
                    </TabPanel>
                    <TabPanel p={2}>
                      <Stack spacing={2} maxH="640px" overflowY="auto">
                        {publicQuestions.length === 0 ? (
                          <Text color="gray.500" fontSize="sm">No public questions found.</Text>
                        ) : publicQuestions.map((q) => (
                          <QuestionCard
                            key={`${q.source_record_id}-${q.question_id}`}
                            question={q}
                            onApprove={onApprove}
                            onApprovePublic={onApprovePublic}
                            onDraft={onDraft}
                            onUpdateDraft={onUpdateDraft}
                            onReject={onReject}
                            onRejectWithComment={onRejectWithComment}
                            canPublishPublic={q.institution === activeInstitution}
                            canReject={true}
                          />
                        ))}
                      </Stack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}

              {(chatting || savingQuestion || savingAdaptiveFeedback || tuningAdaptive) && (
                <Text fontSize="xs" color="gray.400">Working...</Text>
              )}

              {isSystemAdmin && (
                <>
                  <Divider />
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Rejected Question Review Queue</Text>
                    <Button size="xs" onClick={loadAdaptiveQueue} isLoading={loadingQueue || reviewingQueue}>Refresh Queue</Button>
                  </HStack>
                  <Text fontSize="xs" color="gray.400">
                    Approve sends to training. Edit + Approve updates then sends. Deny clears from queue.
                  </Text>
                  {queueError && (
                    <Alert status="warning" bg="orange.900" borderRadius="md">
                      <AlertIcon />
                      {queueError}
                    </Alert>
                  )}
                  <Stack spacing={2} maxH="340px" overflowY="auto">
                    {queueItems.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">No pending rejected questions in queue.</Text>
                    ) : queueItems.map((item) => (
                      <Box key={item.id} borderWidth="1px" borderColor="gray.700" borderRadius="md" p={2} bg="gray.900">
                        <HStack spacing={2} mb={2} flexWrap="wrap">
                          <Badge colorScheme="orange">{item.action || "reject"}</Badge>
                          <Badge>{item.institution || "unknown institution"}</Badge>
                          {item.submittedBy?.name && <Badge colorScheme="cyan">{item.submittedBy.name}</Badge>}
                        </HStack>
                        <Text fontSize="xs" color="gray.400" mb={1}>Original Stem</Text>
                        <Text fontSize="sm" color="gray.100" whiteSpace="pre-wrap">
                          {item?.originalQuestion?.stem || "(no stem)"}
                        </Text>
                        <Text fontSize="xs" color="gray.400" mt={2} mb={1}>Submission Comment</Text>
                        <Text fontSize="sm" color="gray.300" whiteSpace="pre-wrap">
                          {item.explanation || "(no comment)"}
                        </Text>
                        <Textarea
                          mt={2}
                          size="xs"
                          minH="64px"
                          placeholder="Optional admin review note"
                          value={queueNotes[item.id] || ""}
                          onChange={(e) => setQueueNotes((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        />
                        <Textarea
                          mt={2}
                          size="xs"
                          minH="72px"
                          placeholder="Edit stem before approve (for Edit + Approve)"
                          value={queueEdits[item.id] ?? item?.originalQuestion?.stem ?? ""}
                          onChange={(e) => setQueueEdits((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        />
                        <HStack mt={2} spacing={2}>
                          <Button size="xs" colorScheme="green" onClick={() => reviewQueueItem(item, "approve")} isLoading={reviewingQueue}>Approve</Button>
                          <Button size="xs" colorScheme="blue" onClick={() => reviewQueueItem(item, "edit_approve")} isLoading={reviewingQueue}>
                            Edit + Approve
                          </Button>
                          <Button size="xs" colorScheme="red" variant="outline" onClick={() => reviewQueueItem(item, "deny")} isLoading={reviewingQueue}>Deny</Button>
                        </HStack>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
            </VStack>
          </Box>
        </Grid>
      </VStack>
    </Box>
  );
}
